// EDEN · the school calendar's own events, placed on school weeks (owner ruling A2, 21 Oct 2026).
//
// The Year plan used to print each grade's TYPED week tags ("Monthly test", "End of Term 2", "Parent–teacher
// meeting" …). Of the 176 tags whose fact is in the school calendar, most sat on another week (e.g. nine grades
// showed the Term 3 monthly test in Week 3 or 4; the calendar's is Week 7). This module reads the calendar the
// app already bundles (curriculum/_build/calendar-2026-27.json — the same file src/term-weeks.ts reads) and places
// every date by the app's one week rule: a week runs from its first school day to the day before the next week's
// first school day, so a holiday or a weekend belongs to the week it follows and every date is in exactly one week.
//
// For a grade and a school week it returns the chips the screens print: the grade's tests (assessment_windows),
// the SA speaking week (the calendar's own oral window when it names one for the grade — "Oral Test for G1-8, G10-G11",
// 12–15 Jul 2027 — otherwise the week before the SA written week), the two review weeks before each SA speaking week
// (Test Standard §7.3: the two full teaching weeks before the oral week), the first and last
// school week of each term, and every calendar event in that week that concerns the grade. The year plans are not
// changed; only what the screens show. The calendar file changes only through the department's calendar tooling.
//
// Pure: no DOM, no fetch, no clock.

import { assessment_windows, events as calendarEvents, terms as calendarTerms, weeks as calendarWeeks } from "../curriculum/_build/calendar-2026-27.json";
import { isSchoolDay, termWeekRange, type TermSpan } from "./term-weeks";

export interface CalendarChip {
  /** A stable key for tests and for de-duplicating ("monthly-test", "no-school:Water Festival Day" …). */
  code: string;
  /** The words a teacher reads. */
  label: string;
  kind: "term" | "test" | "review" | "event" | "no-school";
  /** The dates it covers, in words ("Mon 23 – Wed 25 Nov"), when the chip is a day or a run of days. */
  when?: string;
}

interface YearWeek { term: number; week: number; start: string; end: string; schoolDays: number }

const spanOf = (term: number): TermSpan => {
  const t = calendarTerms.find((x) => x.index === term);
  if (!t) throw new Error(`calendar-2026-27.json has no term ${term}`);
  return { start_date: t.start, end_date: t.end };
};

/** Every school week of the year, in order, with the dates the app's week rule gives it. */
const YEAR_WEEKS: YearWeek[] = calendarWeeks.map((w) => {
  const term = Number(w.term.slice(1));
  const range = termWeekRange(spanOf(term), w.week);
  return { term, week: w.week, start: range.start, end: range.end, schoolDays: w.school_days };
});

const key = (term: number, week: number): string => `${term}.${week}`;
const WEEK_INDEX = new Map(YEAR_WEEKS.map((w, i) => [key(w.term, w.week), i]));

/** The school week a date falls in, or null before the year starts / after its last week. */
export function schoolWeekOf(date: string): { term: number; week: number } | null {
  const w = YEAR_WEEKS.find((x) => date >= x.start && date <= x.end);
  return w ? { term: w.term, week: w.week } : null;
}

/** The dates a school week runs (the app's rule), or null when the calendar has no such week this year. */
export function schoolWeekDates(term: number, week: number): { start: string; end: string; schoolDays: number } | null {
  const i = WEEK_INDEX.get(key(term, week));
  const w = i === undefined ? null : YEAR_WEEKS[i]!;
  return w ? { start: w.start, end: w.end, schoolDays: w.schoolDays } : null;
}

/** The last school week of a term in this year's calendar (T1 W13, T2 W14, T3 W13), or null. */
export function lastSchoolWeek(term: number): number | null {
  const weeks = YEAR_WEEKS.filter((w) => w.term === term);
  return weeks.length ? weeks[weeks.length - 1]!.week : null;
}

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function parts(iso: string): { dow: number; d: number; m: number } {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  return { dow: new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay(), d, m };
}
function dayWords(iso: string): string {
  const p = parts(iso);
  return `${DOW[p.dow]} ${p.d} ${MONTH[p.m - 1]}`;
}
/** "Thu 29 Oct", "Mon 12 – Tue 13 Apr", "Mon 23 – Wed 25 Nov". */
function runWords(first: string, last: string): string {
  if (first === last) return dayWords(first);
  const a = parts(first);
  const b = parts(last);
  return a.m === b.m ? `${DOW[a.dow]} ${a.d} – ${DOW[b.dow]} ${b.d} ${MONTH[b.m - 1]}` : `${dayWords(first)} – ${dayWords(last)}`;
}
const isWeekday = (iso: string): boolean => {
  const dow = parts(iso).dow;
  return dow !== 0 && dow !== 6;
};

// ── the grade's tests ──────────────────────────────────────────────────────────────────────────────────────────
/** Does an assessment window's label name this grade? ("for all"; "G1-G8, G10-G11"; the oral test's "G1-8, G10-G11";
 *  "G9 & G12"; "G9 NE GTIS"; "SA2 G6"). */
function windowApplies(label: string, grade: number): boolean {
  if (/\bfor all\b/i.test(label)) return true;
  if (/G1-G?8, G10-G11/.test(label)) return (grade >= 1 && grade <= 8) || grade === 10 || grade === 11;
  if (/G9 ?& ?G12/.test(label)) return grade === 9 || grade === 12;
  if (/^G9 NE/.test(label)) return grade === 9;
  if (/^SA2 G6$/.test(label)) return grade === 6;
  return false;
}

const WINDOW_LABEL: Record<string, { code: string; label: string }> = {
  monthly: { code: "monthly-test", label: "Monthly test" },
  sa1: { code: "sa1-written", label: "SA1 written test" },
  sa2: { code: "sa2-written", label: "SA2 written test" },
  mock: { code: "school-mock-exam", label: "School mock exam" },
  national_exam: { code: "national-exam", label: "National exam (GTIS)" },
};

interface Placed { term: number; week: number }
const placed = (term: string, week: number): Placed => ({ term: Number(term.slice(1)), week });

/** The school week before `at` in the year's order (T2 W1 → T1 W13), or null at the start of the year. */
function weekBefore(at: Placed): Placed | null {
  const i = WEEK_INDEX.get(key(at.term, at.week));
  const w = i === undefined || i === 0 ? null : YEAR_WEEKS[i - 1]!;
  return w ? { term: w.term, week: w.week } : null;
}

/** The next school day after `iso` (the calendar file's own answer, via isSchoolDay). */
function nextSchoolDay(iso: string): string {
  let t = Date.parse(`${iso}T12:00:00Z`);
  do t += 86_400_000;
  while (!isSchoolDay(new Date(t).toISOString().slice(0, 10)));
  return new Date(t).toISOString().slice(0, 10);
}

/** The speaking tests the calendar itself names ("Oral Test for G1-8, G10-G11"), each on the week of its first day. */
interface OralWindow { label: string; start: string; end: string; at: Placed }
const ORAL_WINDOWS: OralWindow[] = (() => {
  const out: OralWindow[] = [];
  for (const e of calendarEvents) {
    if (!e.school_day || !/^Oral Test\b/i.test(e.event)) continue;
    const last = out[out.length - 1];
    if (last && last.label === e.event && nextSchoolDay(last.end) === e.date) {
      last.end = e.date; // the next school day of the same window (a weekend or a holiday between two days does not split it)
      continue;
    }
    const week = schoolWeekOf(e.date);
    if (week) out.push({ label: e.event, start: e.date, end: e.date, at: week });
  }
  return out;
})();

/** The week a grade sits its speaking test for the SA written in `written`: the calendar's own oral window when it names one
 *  for this grade in the same term, before the written week; otherwise the week before the written week (Test Standard §7.3). */
function speakingFor(grade: number, written: Placed): { at: Placed | null; when?: string } {
  const writtenAt = WEEK_INDEX.get(key(written.term, written.week)) ?? -1;
  const named = ORAL_WINDOWS.filter((o) => windowApplies(o.label, grade) && o.at.term === written.term
    && (WEEK_INDEX.get(key(o.at.term, o.at.week)) ?? Infinity) < writtenAt).pop();
  return named ? { at: named.at, when: runWords(named.start, named.end) } : { at: weekBefore(written) };
}

/** The two FULL (five-school-day) weeks before the speaking week — a one- or two-day week does not count. */
function reviewWeeksBefore(oral: Placed): Placed[] {
  const out: Placed[] = [];
  let at = weekBefore(oral);
  while (at && out.length < 2) {
    const i = WEEK_INDEX.get(key(at.term, at.week))!;
    if (YEAR_WEEKS[i]!.schoolDays >= 5) out.push(at);
    at = weekBefore(at);
  }
  return out;
}

function testChips(grade: number, at: Placed): CalendarChip[] {
  const out: CalendarChip[] = [];
  // A grade sits ONE SA1 and ONE SA2: the earliest window that names it. Grade 6 is named by both "SA2 G6" (22–23 Jul)
  // and "SA2 G1-G8, G10-G11" (28–30 Jul); its own, earlier one is its SA2 — the department's calendar fit report
  // (curriculum/_build/project_calendar.py) takes the same first window.
  const saSeen = new Set<string>();
  for (const w of assessment_windows) {
    if (!windowApplies(w.label, grade)) continue;
    if (w.kind === "sa1" || w.kind === "sa2") {
      if (saSeen.has(w.kind)) continue;
      saSeen.add(w.kind);
    }
    const written = placed(w.term, w.week);
    const names = WINDOW_LABEL[w.kind];
    if (names && written.term === at.term && written.week === at.week) {
      out.push({ code: names.code, label: names.label, kind: "test", when: runWords(w.start, w.end) });
    }
    if (w.kind === "sa1" || w.kind === "sa2") {
      const sa = w.kind === "sa1" ? "SA1" : "SA2";
      const { at: oral, when } = speakingFor(grade, written);
      if (oral && oral.term === at.term && oral.week === at.week) {
        out.push({ code: `${w.kind}-oral`, label: `${sa} speaking test`, kind: "test", ...(when ? { when } : {}) });
      }
      if (oral && reviewWeeksBefore(oral).some((r) => r.term === at.term && r.week === at.week)) {
        out.push({ code: `${w.kind}-review`, label: `Review before ${sa} (no new content)`, kind: "review" });
      }
    }
  }
  return out;
}

// ── the term's first and last school week ──────────────────────────────────────────────────────────────────────
function termChips(at: Placed): CalendarChip[] {
  const out: CalendarChip[] = [];
  if (at.week === 1) out.push({ code: `start-term-${at.term}`, label: `Start of Term ${at.term}`, kind: "term" });
  if (at.week === lastSchoolWeek(at.term)) out.push({ code: `end-of-term-${at.term}`, label: `End of Term ${at.term}`, kind: "term" });
  return out;
}

// ── named days and days with no school ─────────────────────────────────────────────────────────────────────────
/** School-day (or named) calendar entries a teacher is told about, by the calendar's own name. */
const NAMED_DAYS: Record<string, string> = {
  "PTM 1": "Parent–teacher meeting",
  "PTM2": "Parent–teacher meeting",
  "Chinese New Year": "Chinese New Year",
  "Christmas Day": "Christmas Day",
  "Games Day": "Games Day",
  "Student Excursion": "Student excursion",
  "Results Out": "Results out",
  "Prize Giving & Graduation Day": "Prize giving and graduation day",
  "Class Party": "Class party",
};
/** "G2 Camp", "G3 Camp", "G1 Camp", "G9 Camp": a school-day entry that is one grade's camp, shown to that grade only. */
const CAMP = /^G(\d+) Camp$/;
/** Entries that belong to particular grades without being a plain camp: each is shown to those grades, under that label, and to
 *  no one else ("G9 Camp, G6 TE Result Out" is two facts on one day; the Grade 9 half joins that camp's chip). */
const GRADE_ENTRIES: Record<string, ReadonlyArray<{ grade: number; label: string }>> = {
  "G3 Camp Prep": [{ grade: 3, label: "Grade 3 camp preparation" }],
  "G6 Transition Exam": [{ grade: 6, label: "Grade 6 Transition Exam" }],
  "G9 Camp, G6 TE Result Out": [{ grade: 9, label: "Grade 9 camp" }, { grade: 6, label: "Grade 6 Transition Exam results out" }],
  "G9 NE Result Out": [{ grade: 9, label: "Grade 9 national exam results out" }],
};
/** The label a grade-scoped entry shows THIS grade; null when it is another grade's; undefined for an ordinary entry. */
function gradeEntry(name: string, grade: number): string | null | undefined {
  const camp = CAMP.exec(name);
  const list = camp ? [{ grade: Number(camp[1]), label: `Grade ${camp[1]} camp` }] : GRADE_ENTRIES[name];
  return list ? list.find((x) => x.grade === grade)?.label ?? null : undefined;
}
/** The calendar file's spelling of a few holiday names, in plain words (display only). */
const NAME_FIX: Record<string, string> = {
  "King Father' s Commemoration Day": "King Father's Commemoration Day",
  "Royal Plaughing Ceremony": "Royal Ploughing Ceremony",
};

const yearFirst = YEAR_WEEKS[0]!.start;
const yearLast = YEAR_WEEKS[YEAR_WEEKS.length - 1]!.end;

interface Entry { date: string; name: string | null; schoolDay: boolean }
/** Every weekday inside the school year that has a calendar entry or that the file leaves out of `school_days`. */
const ENTRIES: Entry[] = (() => {
  const named = new Map<string, { names: string[]; schoolDay: boolean }>();
  for (const e of calendarEvents) {
    if (e.date < yearFirst || e.date > yearLast || !isWeekday(e.date)) continue;
    const have = named.get(e.date) ?? { names: [], schoolDay: e.school_day };
    have.names.push(e.event);
    have.schoolDay = have.schoolDay && e.school_day;
    named.set(e.date, have);
  }
  const out: Entry[] = [];
  for (const [date, have] of named) {
    for (const name of have.names) out.push({ date, name: NAME_FIX[name] ?? name, schoolDay: have.schoolDay });
  }
  // A weekday the file leaves out of `school_days` without naming it (this year: 13 Oct, 8 Jan, 12–13 Apr, 21 May). Inside the
  // year isSchoolDay() answers from that same file, so a weekday it rejects is a day off.
  const firstDay = new Date(`${yearFirst}T12:00:00Z`).getTime();
  const lastDay = new Date(`${yearLast}T12:00:00Z`).getTime();
  for (let t = firstDay; t <= lastDay; t += 86_400_000) {
    const date = new Date(t).toISOString().slice(0, 10);
    if (isWeekday(date) && !named.has(date) && !isSchoolDay(date)) out.push({ date, name: null, schoolDay: false });
  }
  return out.sort((a, b) => a.date.localeCompare(b.date));
})();

/** A named day off whose calendar name reads better in a sentence. */
const NAMED_OFF: Record<string, string> = {
  "Water Festival Day": "Water Festival",
  "Pchum Ben Festival": "Pchum Ben festival",
  "Staff Training": "Staff training",
};

/** The chips for named days and days off in one school week: a run of consecutive days with one name is one chip. */
function dayChips(grade: number, at: Placed): CalendarChip[] {
  const inWeek = ENTRIES.filter((e) => {
    const w = schoolWeekOf(e.date);
    return !!w && w.term === at.term && w.week === at.week;
  });
  const out: CalendarChip[] = [];
  // Entries with one label in one week are one chip, covering the first to the last of their dates.
  const groups = new Map<string, { label: string; code: string; kind: CalendarChip["kind"]; dates: string[] }>();
  for (const e of inWeek) {
    const own = e.name ? gradeEntry(e.name, grade) : undefined;
    if (own === null) continue; // another grade's camp, exam or results
    if (e.schoolDay) {
      const label = own ?? (e.name ? NAMED_DAYS[e.name] : undefined);
      if (!label) continue; // a school-day entry that is not on the list (fire drill, community meeting …) is not shown
      const g = groups.get(`day:${label}`) ?? { label, code: `day:${e.name}`, kind: "event" as const, dates: [] };
      g.dates.push(e.date);
      groups.set(`day:${label}`, g);
    } else {
      const label = e.name ? `No school: ${NAMED_OFF[e.name] ?? e.name}` : "No school";
      const g = groups.get(`off:${label}`) ?? { label, code: `no-school:${e.name ?? ""}`, kind: "no-school" as const, dates: [] };
      g.dates.push(e.date);
      groups.set(`off:${label}`, g);
    }
  }
  for (const g of groups.values()) {
    g.dates.sort();
    out.push({ code: g.code, label: g.label, kind: g.kind, when: runWords(g.dates[0]!, g.dates[g.dates.length - 1]!) });
  }
  return out;
}

/** Everything the school calendar says about one school week for a grade, in the order a teacher reads it:
 *  the term's start or end, the tests and their review weeks, then named days, then days with no school.
 *  Empty for a week the calendar does not have (the year plan's overflow weeks). */
export function calendarChips(grade: number, term: number, week: number): CalendarChip[] {
  const at = { term, week };
  if (!WEEK_INDEX.has(key(term, week))) return [];
  const days = dayChips(grade, at);
  return [
    ...termChips(at),
    ...testChips(grade, at),
    ...days.filter((c) => c.kind === "event"),
    ...days.filter((c) => c.kind === "no-school"),
  ];
}

/** The year plans' typed week tags that state a fact the school calendar owns. The screens no longer print these
 *  (the calendar's chips replace them); the department's own tags — mocks, observations, spelling bee, showcase,
 *  Christmas programme, camp preparation, marking, report cards — are not on this list and stay. */
export const CALENDAR_BACKED_TAGS: ReadonlySet<string> = new Set([
  "monthly-test", "sa1-oral", "sa1-written", "sa1-freeze", "sa2", "sa2-written", "sa2-oral", "oral-test",
  "ptm-1", "ptm-2", "end-of-term-1", "end-of-term-2", "end-of-term-3", "start-term-2", "staff-training-overlap",
  "khmer-new-year-days", "khmer-new-year-boundary", "khmer-new-year-adjacent", "chinese-new-year-days", "chinese-new-year-adjacent",
  "water-festival", "kings-coronation-day", "kings-father-commemoration-day", "queens-birthday", "victory-day", "new-year-day",
  "no-school-days", "ne-gtis", "results", "prize-giving-day", "graduation", "graduation-window", "class-party",
  "school-mock-ex-g9", "g9-exam-no-class",
]);
